import { Link } from 'react-router-dom';
import usePageMeta from '../lib/usePageMeta';
import PageHero from '../components/PageHero';
import Icon from '../components/Icon';
import WhyUs from '../components/WhyUs';
import { SectionHead, IndustryChips } from '../components/Blocks';
import { STATS, VALUES, TIMELINE, TEAM } from '../data';
import { IMG, u } from '../images';

export default function About() {
  usePageMeta('About Us', 'Learn about Origo Private Limited — our story, mission, values and the specialist team behind 120+ websites, apps, brands and animations.');

  return (
    <>
      <PageHero
        eyebrow="About us"
        title="A technology partner built on craft, honesty and results"
        description="Origo Private Limited is a full-service digital agency. We combine strategy, design and engineering to help businesses launch faster and grow smarter."
        crumbs={[{ label: 'About' }]}
      />

      <section className="section bg-white">
        <div className="container-x grid items-start gap-14 lg:grid-cols-2">
          <div data-anim="left">
            <span className="eyebrow">Our story</span>
            <h2 className="h2 mt-5 text-ink">Started by <span className="text-accent">engineers</span> who wanted agency work to be <span className="text-outline">better</span></h2>
            <div className="mt-6 space-y-4 text-lg leading-relaxed text-slate-600">
              <p>Origo Private Limited began in 2017 with two engineers and a simple belief: businesses deserve a technology partner that is senior, transparent and accountable for outcomes.</p>
              <p>Since then we have grown into a team of 40 specialists across engineering, design, animation and marketing, delivering more than 120 projects for clients in over a dozen industries.</p>
              <p>Today we operate as a remote-first studio, which lets us hire the best people wherever they are and work in the time zones our clients need.</p>
            </div>
          </div>
          <div className="grid gap-6 sm:grid-cols-2" data-anim="right" data-stagger>
            <div className="rounded-3xl bg-gradient-to-br from-brand to-ink p-8 text-white">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-brand-lime/20 text-brand-lime"><Icon name="rocket" /></span>
              <h3 className="mt-5 text-xl font-bold">Our mission</h3>
              <p className="mt-3 text-white/75">To make world-class digital products accessible to ambitious businesses of every size.</p>
            </div>
            <div className="rounded-3xl bg-brand-soft p-8">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-brand text-white"><Icon name="chart" /></span>
              <h3 className="mt-5 text-xl font-bold text-ink">Our vision</h3>
              <p className="mt-3 text-slate-600">To be the most trusted end-to-end digital partner for growing companies worldwide.</p>
            </div>
            <div className="rounded-3xl bg-brand-soft p-8 sm:col-span-2">
              <h3 className="text-xl font-bold text-ink">What makes us different</h3>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                {['Senior specialists on every project', 'One team for design, code, motion & marketing', 'Fixed quotes, no hidden costs', 'You own 100% of the code and assets'].map((t) => (
                  <li key={t} className="flex gap-2 text-slate-700"><Icon name="check" className="mt-0.5 h-5 w-5 shrink-0 text-brand" strokeWidth={2.6} />{t}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="container-x mt-20">
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl bg-brand/15 lg:grid-cols-4" data-anim="up">
            {STATS.map((s) => (
              <div key={s.label} className="bg-brand-soft px-6 py-9 text-center">
                <p className="font-display text-4xl font-bold text-brand sm:text-5xl" data-count={s.value} data-suffix={s.suffix}>0</p>
                <p className="mt-2 text-sm font-medium text-slate-600">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-brand-soft">
        <div className="container-x">
          <SectionHead eyebrow="Our values" title="The principles behind every project" />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-anim="flip" data-stagger>
            {VALUES.map((v) => (
              <div key={v.title} className="lift rounded-3xl bg-white p-7 shadow-sm ring-1 ring-brand/10">
                <span className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-soft text-brand"><Icon name={v.icon} className="h-7 w-7" /></span>
                <h3 className="mt-5 text-xl font-bold text-ink">{v.title}</h3>
                <p className="mt-2 leading-relaxed text-slate-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-x">
          <SectionHead eyebrow="Our journey" title="Nine years of building, learning and growing" />
          <ol className="relative mx-auto mt-14 max-w-4xl space-y-10" data-line-wrap>
            <span className="absolute bottom-0 left-[27px] top-0 w-0.5 bg-brand/10 md:left-1/2" aria-hidden="true" />
            <span data-line className="absolute bottom-0 left-[27px] top-0 w-0.5 bg-gradient-to-b from-brand-soft to-brand md:left-1/2" aria-hidden="true" />
            {TIMELINE.map((t, i) => (
              <li key={t.year} className="relative flex gap-6 md:grid md:grid-cols-2 md:gap-14" data-anim={i % 2 ? 'right' : 'left'}>
                <span className={`relative z-10 grid h-14 w-14 shrink-0 place-items-center rounded-full bg-brand text-sm font-bold text-white ring-8 ring-white md:absolute md:left-1/2 md:-translate-x-1/2`}>{t.year.slice(2)}</span>
                <div className={`rounded-3xl border border-brand/10 bg-brand-soft/60 p-6 ${i % 2 ? 'md:col-start-2' : 'md:col-start-1 md:text-right'}`}>
                  <p className="text-sm font-semibold text-brand">{t.year}</p>
                  <h3 className="mt-1 text-xl font-bold text-ink">{t.title}</h3>
                  <p className="mt-2 text-slate-600">{t.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <WhyUs />

      <section className="section bg-white">
        <div className="container-x">
          <SectionHead eyebrow="Our team" title="Meet the people behind the work" text="A senior, multidisciplinary team that cares about your results." />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-anim="up" data-stagger>
            {TEAM.map((m) => (
              <div key={m.name} className="lift flex gap-5 rounded-3xl border border-brand/10 bg-brand-soft/50 p-6">
                <img src={u(IMG.team[TEAM.indexOf(m) % IMG.team.length], 300)} alt={m.name} loading="lazy" className="h-20 w-20 shrink-0 rounded-2xl object-cover object-top" />
                <div>
                  <h3 className="text-lg font-bold text-ink">{m.name}</h3>
                  <p className="text-sm font-medium text-brand">{m.role}</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{m.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-brand-soft">
        <div className="container-x text-center">
          <SectionHead eyebrow="Industries" title="Sectors we know well" />
          <div className="mt-10"><IndustryChips /></div>
          <div className="mt-12" data-anim="up"><Link to="/contact" className="btn-green">Work with us <Icon name="arrow" className="h-4 w-4" /></Link></div>
        </div>
      </section>
    </>
  );
}
